"use client";

import { ArrowLeft, Calendar, Filter, MapPin, Search, Star } from "lucide-react";
import Link from "next/link";
import { useMemo, useState } from "react";
import { ImageWithFallback } from "@/figma/components/figma/ImageWithFallback";
import { getStaticDirectoryLandingData } from "@/lib/directory-static-data";
import type { DirectoryLandingData } from "@/lib/directory-types";

interface DirectoryPageProps {
  data?: DirectoryLandingData;
}

export function DirectoryPage({ data = getStaticDirectoryLandingData() }: DirectoryPageProps) {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredWineries = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    if (!term) return data.wineries;
    return data.wineries.filter((winery) => {
      return (
        winery.name.toLowerCase().includes(term) ||
        winery.region.toLowerCase().includes(term) ||
        winery.description.toLowerCase().includes(term)
      );
    });
  }, [data.wineries, searchQuery]);

  const filteredFeaturedWines = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    if (!term) return data.featuredWines;
    return data.featuredWines.filter((wine) => {
      return (
        wine.name.toLowerCase().includes(term) ||
        wine.winery.toLowerCase().includes(term) ||
        wine.region.toLowerCase().includes(term)
      );
    });
  }, [data.featuredWines, searchQuery]);

  return (
    <div className="min-h-screen" style={{ fontFamily: "var(--font-body)" }}>
      <div style={{ backgroundColor: "var(--linen)", borderBottom: "1px solid var(--stone)" }}>
        <div className="mx-auto max-w-screen-2xl px-8 py-8 lg:px-16">
          <Link
            href="/"
            className="inline-flex items-center gap-2 transition-opacity duration-200 hover:opacity-50"
            style={{
              fontFamily: "var(--font-body)",
              fontSize: "var(--text-sm)",
              color: "var(--charcoal)",
              fontWeight: 400,
            }}
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </div>
      </div>

      <section style={{ backgroundColor: "var(--paper)" }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="max-w-4xl">
              <div className="mb-8">
                <span
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: "var(--text-sm)",
                    fontWeight: 300,
                    color: "var(--slate)",
                    letterSpacing: "0.05em",
                  }}
                >
                  02
                </span>
              </div>

              <h1
                className="mb-8"
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "var(--text-5xl)",
                  fontWeight: 300,
                  lineHeight: "var(--line-height-tight)",
                  color: "var(--charcoal)",
                  letterSpacing: "-0.02em",
                }}
              >
                Directory
              </h1>

              <p
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: "var(--text-xl)",
                  lineHeight: "var(--line-height-body)",
                  color: "var(--slate)",
                  fontWeight: 300,
                  maxWidth: "720px",
                }}
              >
                The definitive database of Israeli wine. Search and filter wineries, wines, and vintage ratings.
              </p>
            </div>

            <div className="mt-16 max-w-2xl">
              <div className="relative">
                <Search
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 transform"
                  style={{ color: "var(--slate)" }}
                />
                <input
                  type="text"
                  placeholder="Search wineries, wines, or regions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full border py-4 pl-12 pr-4 transition-colors duration-200 focus:border-[var(--charcoal)] focus:outline-none"
                  style={{
                    borderColor: "var(--stone)",
                    backgroundColor: "var(--paper)",
                    fontFamily: "var(--font-body)",
                    fontSize: "var(--text-base)",
                  }}
                />
                <div
                  className="absolute right-4 top-1/2 flex -translate-y-1/2 transform items-center gap-2 px-4 py-2"
                  style={{
                    backgroundColor: "var(--charcoal)",
                    color: "var(--paper)",
                    fontFamily: "var(--font-body)",
                    fontSize: "var(--text-sm)",
                  }}
                >
                  <Filter size={16} />
                  Search
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section style={{ backgroundColor: "var(--linen)" }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="mb-16">
              <h2
                className="mb-4"
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "var(--text-3xl)",
                  fontWeight: 300,
                  lineHeight: "var(--line-height-tight)",
                  color: "var(--charcoal)",
                  letterSpacing: "-0.02em",
                }}
              >
                Featured Wineries
              </h2>
              <p
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: "var(--text-base)",
                  lineHeight: "var(--line-height-body)",
                  color: "var(--slate)",
                  fontWeight: 300,
                }}
              >
                Browse a curated selection of Israel&apos;s top producers
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {filteredWineries.map((winery) => (
                <Link
                  key={winery.slug}
                  href={`/directory/winery/${winery.slug}`}
                  style={{ backgroundColor: "var(--paper)" }}
                  className="group cursor-pointer transition-all duration-300 hover:shadow-lg"
                >
                  <div className="aspect-[4/3] overflow-hidden" style={{ backgroundColor: "var(--stone)" }}>
                    <ImageWithFallback
                      src={winery.image}
                      alt={winery.name}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-8">
                    <div className="mb-3 flex items-start justify-between">
                      <h3
                        style={{
                          fontFamily: "var(--font-display)",
                          fontSize: "var(--text-xl)",
                          fontWeight: 300,
                          color: "var(--charcoal)",
                        }}
                      >
                        {winery.name}
                      </h3>
                      <div className="ml-2 flex items-center gap-1">
                        <Star size={14} style={{ color: "var(--oxblood)", fill: "var(--oxblood)" }} />
                        <span
                          style={{
                            fontFamily: "var(--font-body)",
                            fontSize: "var(--text-sm)",
                            color: "var(--charcoal)",
                            fontWeight: 500,
                          }}
                        >
                          {winery.rating ?? "-"}
                        </span>
                      </div>
                    </div>
                    <div className="mb-4 flex items-center gap-3">
                      <p
                        className="flex items-center gap-1"
                        style={{
                          fontFamily: "var(--font-body)",
                          fontSize: "var(--text-xs)",
                          color: "var(--slate)",
                          fontWeight: 400,
                        }}
                      >
                        <MapPin size={12} />
                        {winery.region}
                      </p>
                      {winery.established ? (
                        <>
                          <span style={{ color: "var(--stone)" }}>•</span>
                          <p
                            style={{
                              fontFamily: "var(--font-body)",
                              fontSize: "var(--text-xs)",
                              color: "var(--slate)",
                              fontWeight: 400,
                            }}
                          >
                            Est. {winery.established}
                          </p>
                        </>
                      ) : null}
                    </div>
                    <p
                      style={{
                        fontFamily: "var(--font-body)",
                        fontSize: "var(--text-sm)",
                        lineHeight: "var(--line-height-body)",
                        color: "var(--slate)",
                        fontWeight: 300,
                      }}
                    >
                      {winery.description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section style={{ backgroundColor: "var(--paper)" }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="mb-16">
              <h2
                className="mb-4"
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "var(--text-3xl)",
                  fontWeight: 300,
                  lineHeight: "var(--line-height-tight)",
                  color: "var(--charcoal)",
                  letterSpacing: "-0.02em",
                }}
              >
                Highly Rated Wines
              </h2>
              <p
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: "var(--text-base)",
                  lineHeight: "var(--line-height-body)",
                  color: "var(--slate)",
                  fontWeight: 300,
                }}
              >
                Top-scoring wines from recent vintages
              </p>
            </div>

            <div className="grid gap-6">
              {filteredFeaturedWines.map((wine) => (
                <Link
                  key={wine.slug}
                  href={`/directory/wine/${wine.slug}`}
                  className="cursor-pointer border p-10 transition-all duration-200 hover:bg-[var(--linen)]"
                  style={{ borderColor: "var(--stone)" }}
                >
                  <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex-1">
                      <div className="mb-4 flex items-start gap-4">
                        <div className="flex-1">
                          <h3
                            className="mb-2"
                            style={{
                              fontFamily: "var(--font-display)",
                              fontSize: "var(--text-2xl)",
                              fontWeight: 300,
                              color: "var(--charcoal)",
                            }}
                          >
                            {wine.name}{wine.vintage ? ` ${wine.vintage}` : ""}
                          </h3>
                          <p
                            style={{
                              fontFamily: "var(--font-body)",
                              fontSize: "var(--text-sm)",
                              color: "var(--slate)",
                              fontWeight: 400,
                            }}
                          >
                            {wine.winery} • {wine.region}
                          </p>
                        </div>
                        <div className="px-4 py-2 text-center" style={{ backgroundColor: "var(--oxblood)" }}>
                          <div
                            style={{
                              fontFamily: "var(--font-display)",
                              fontSize: "var(--text-2xl)",
                              fontWeight: 300,
                              color: "var(--paper)",
                              lineHeight: 1,
                            }}
                          >
                            {wine.rating ?? "-"}
                          </div>
                          <div
                            style={{
                              fontFamily: "var(--font-body)",
                              fontSize: "var(--text-xs)",
                              color: "var(--paper)",
                              fontWeight: 400,
                              marginTop: "4px",
                            }}
                          >
                            Points
                          </div>
                        </div>
                      </div>
                      <p
                        className="mb-4"
                        style={{
                          fontFamily: "var(--font-body)",
                          fontSize: "var(--text-sm)",
                          lineHeight: "var(--line-height-body)",
                          color: "var(--slate)",
                          fontWeight: 300,
                        }}
                      >
                        {wine.description}
                      </p>
                    </div>
                    <div className="lg:text-right">
                      <div
                        className="mb-2"
                        style={{
                          fontFamily: "var(--font-body)",
                          fontSize: "var(--text-xs)",
                          color: "var(--slate)",
                          fontWeight: 500,
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                        }}
                      >
                        Current Price
                      </div>
                      <div
                        style={{
                          fontFamily: "var(--font-display)",
                          fontSize: "var(--text-2xl)",
                          fontWeight: 300,
                          color: "var(--charcoal)",
                        }}
                      >
                        {wine.price ?? "-"}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section style={{ backgroundColor: "var(--linen)" }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="mb-12 flex items-center gap-3">
              <Calendar size={24} style={{ color: "var(--oxblood)" }} />
              <h2
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "var(--text-3xl)",
                  fontWeight: 300,
                  lineHeight: "var(--line-height-tight)",
                  color: "var(--charcoal)",
                }}
              >
                Vintage Guide
              </h2>
            </div>

            <div className="overflow-hidden border" style={{ borderColor: "var(--stone)" }}>
              {data.vintageGuide.map((vintage, index) => (
                <div
                  key={vintage.year}
                  className="grid gap-4 px-8 py-6 md:grid-cols-[120px_180px_1fr]"
                  style={{
                    backgroundColor: index % 2 === 0 ? "var(--paper)" : "var(--linen)",
                    borderBottom:
                      index < data.vintageGuide.length - 1 ? "1px solid var(--stone)" : "none",
                  }}
                >
                  <div
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: "var(--text-2xl)",
                      fontWeight: 300,
                      color: "var(--charcoal)",
                    }}
                  >
                    {vintage.year}
                  </div>
                  <div
                    style={{
                      fontFamily: "var(--font-body)",
                      fontSize: "var(--text-sm)",
                      fontWeight: 500,
                      color: "var(--oxblood)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {vintage.rating}
                  </div>
                  <div
                    style={{
                      fontFamily: "var(--font-body)",
                      fontSize: "var(--text-sm)",
                      fontWeight: 300,
                      color: "var(--slate)",
                    }}
                  >
                    {vintage.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
